import React, { useEffect, useMemo, useState } from "react";
import { API } from "aws-amplify";
import {
    View,
    Heading,
    SelectField,
    Loader,
    Table,
    TableCell,
    TableHead,
    TableRow,
    TableBody,
    Link,
    Flex,
    Text,
    Badge,
    Image,
    useAuthenticator,
} from "@aws-amplify/ui-react";

const TIME_PERIODS = [
    { value: "1week", label: "Last 1 Week" },
    { value: "2weeks", label: "Last 2 Weeks" },
    { value: "1month", label: "Last 1 Month" },
    { value: "3months", label: "Last 3 Months" },
    { value: "6months", label: "Last 6 Months" },
    { value: "allTime", label: "All Time" },
];

const BUCKET_LABEL = {
    D: "Daily (≤ 36h)",
    W: "Weekly (≤ 9d)",
    M: "Monthly (≤ 35d)",
    Q: "Quarterly (≤ 100d)",
    H: "Half-year (≤ 200d)",
    Y: "Yearly (≤ 400d)",
    stale: "Stale (> 400d)",
};
const BUCKET_ORDER = ["D", "W", "M", "Q", "H", "Y", "stale"];

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toISOString().slice(0, 16).replace("T", " ");
    } catch (_) {
        return iso;
    }
}

function strippedProfileUrl(url) {
    if (!url) return "";
    return String(url).split("?")[0];
}

function initials(name, email) {
    const src = (name || email || "?").trim();
    const parts = src.split(/\s+|@/).filter(Boolean);
    const a = parts[0] ? parts[0][0] : "?";
    const b = parts[1] ? parts[1][0] : "";
    return (a + b).toUpperCase();
}

function Avatar({ user }) {
    const url = user.avatarUrl;
    if (url && /^https?:\/\//.test(url)) {
        return (
            <Image
                src={url}
                alt={user.name || user.email || "avatar"}
                width="40px"
                height="40px"
                borderRadius="50%"
                objectFit="cover"
            />
        );
    }
    return (
        <View
            width="40px"
            height="40px"
            borderRadius="50%"
            backgroundColor="#e0e0e0"
            color="#555"
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.8rem",
                fontWeight: 600,
            }}
        >
            {initials(user.name, user.email)}
        </View>
    );
}

function SegmentTable({ rows, primaryField, primaryLabel }) {
    if (!rows || rows.length === 0) {
        return <Text>No users in this segment for the selected window.</Text>;
    }
    return (
        <Table caption="" highlightOnHover variation="bordered" size="small">
            <TableHead>
                <TableRow>
                    <TableCell as="th">Photo</TableCell>
                    <TableCell as="th">Name</TableCell>
                    <TableCell as="th">Email</TableCell>
                    <TableCell as="th">LinkedIn Profile</TableCell>
                    <TableCell as="th">Followers</TableCell>
                    <TableCell as="th">{primaryLabel}</TableCell>
                    <TableCell as="th">Last Active</TableCell>
                    <TableCell as="th">Last Task</TableCell>
                    <TableCell as="th">Last Campaign</TableCell>
                    <TableCell as="th">UUID</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((u) => (
                    <TableRow key={u.uuid}>
                        <TableCell>
                            <Avatar user={u} />
                        </TableCell>
                        <TableCell>
                            <Text>{u.name || "—"}</Text>
                            {u.headline ? (
                                <Text fontSize="0.7rem" color="font.tertiary">
                                    {u.headline}
                                </Text>
                            ) : null}
                        </TableCell>
                        <TableCell>
                            {u.email ? (
                                <Link href={`mailto:${u.email}`}>{u.email}</Link>
                            ) : (
                                "—"
                            )}
                        </TableCell>
                        <TableCell>
                            {u.profileUrl ? (
                                <Link href={u.profileUrl} isExternal>
                                    {strippedProfileUrl(u.profileUrl)}
                                </Link>
                            ) : (
                                "—"
                            )}
                        </TableCell>
                        <TableCell>{u.followers != null ? u.followers : "—"}</TableCell>
                        <TableCell>
                            <strong>{fmtDate(u[primaryField])}</strong>
                        </TableCell>
                        <TableCell>{fmtDate(u.lastActiveAt)}</TableCell>
                        <TableCell>{fmtDate(u.lastTaskAt)}</TableCell>
                        <TableCell>{fmtDate(u.lastCampaignAt)}</TableCell>
                        <TableCell>
                            <Text
                                fontSize="0.7rem"
                                color="font.tertiary"
                                style={{ fontFamily: "monospace" }}
                            >
                                {u.uuid}
                            </Text>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function Segment({ title, rows, total, primaryField, primaryLabel }) {
    return (
        <View style={{ marginTop: "1.5rem" }}>
            <Heading level={5}>
                {title} ({total || 0})
            </Heading>
            <SegmentTable rows={rows} primaryField={primaryField} primaryLabel={primaryLabel} />
        </View>
    );
}

export function MostActivePlatformUsersReport() {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [timePeriod, setTimePeriod] = useState("1week");
    const { user } = useAuthenticator((context) => [context.user]);

    useEffect(() => {
        let cancelled = false;
        async function fetchReport() {
            setIsLoading(true);
            try {
                const result = await API.get("gcAPI", "/dashboard-profiles-report", {
                    headers: {
                        Authorization: `${user.signInUserSession.idToken.jwtToken}`,
                    },
                    queryStringParameters: {
                        reportType: "activeUsers",
                        timePeriod,
                        topN: 50,
                    },
                });
                if (!cancelled) setData(result);
            } catch (err) {
                console.error("Active users report failed:", err);
                if (!cancelled) setData(null);
            }
            if (!cancelled) setIsLoading(false);
        }
        fetchReport();
        return () => {
            cancelled = true;
        };
    }, [timePeriod, user]);

    const counts = data && data.counts ? data.counts : {};
    const totals = data && data.totals ? data.totals : {};
    const segments = data && data.segments ? data.segments : {};

    const totalAll = useMemo(
        () => BUCKET_ORDER.reduce((acc, b) => acc + (counts[b] || 0), 0),
        [counts]
    );

    return (
        <View>
            <Heading level={3}>Most Active Platform Users</Heading>
            <Text>
                Activity is tracked on three signals on each user record: <code>lastActiveAt</code>{" "}
                (any deliberate touch of the system, including fetching the task feed),{" "}
                <code>lastTaskAt</code> (task completed/skipped), and{" "}
                <code>lastCampaignAt</code> (campaign created).
            </Text>

            <SelectField
                label="Time Window"
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
            >
                {TIME_PERIODS.map((p) => (
                    <option key={p.value} value={p.value}>
                        {p.label}
                    </option>
                ))}
            </SelectField>

            {isLoading ? (
                <Loader variation="linear" />
            ) : !data ? (
                <Text>Failed to load report.</Text>
            ) : (
                <>
                    <Heading level={5} style={{ marginTop: "1rem" }}>
                        Bucket counts (by <code>lastActiveAt</code> across all {totalAll} users)
                    </Heading>
                    <Flex wrap="wrap" gap="0.5rem" style={{ margin: "0.5rem 0 1rem" }}>
                        {BUCKET_ORDER.map((b) => (
                            <Badge key={b} variation={b === "stale" ? "warning" : "info"}>
                                {BUCKET_LABEL[b]}: {counts[b] || 0}
                            </Badge>
                        ))}
                    </Flex>

                    <Segment
                        title="Task Doers"
                        rows={segments.taskDoers}
                        total={totals.taskDoers}
                        primaryField="lastTaskAt"
                        primaryLabel="Last Task At"
                    />
                    <Segment
                        title="Campaign Creators"
                        rows={segments.campaignCreators}
                        total={totals.campaignCreators}
                        primaryField="lastCampaignAt"
                        primaryLabel="Last Campaign At"
                    />
                    <Segment
                        title="Lurkers"
                        rows={segments.lurkers}
                        total={totals.lurkers}
                        primaryField="lastActiveAt"
                        primaryLabel="Last Active At"
                    />
                </>
            )}
        </View>
    );
}
